import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';

import { BulkActionType } from '../constants';

interface ActionCopy {
  title: string;
  confirmBody: string;
  confirmLabel: string;
}

function getCopy(actionType: BulkActionType, count: number): ActionCopy {
  switch (actionType) {
    case BulkActionType.FACTURADO:
      return {
        title: 'Marcar como facturado',
        confirmBody: `¿Marcar ${count} protocolos como facturados?`,
        confirmLabel: 'Marcar como facturado',
      };
    case BulkActionType.NO_FACTURADO:
      return {
        title: 'Marcar como no facturado',
        confirmBody: `¿Marcar ${count} protocolos como no facturados?`,
        confirmLabel: 'Marcar como no facturado',
      };
    case BulkActionType.COBRADO:
      return {
        title: 'Marcar como cobrado',
        confirmBody: `¿Marcar ${count} protocolos como cobrados?`,
        confirmLabel: 'Marcar como cobrado',
      };
    case BulkActionType.NO_COBRADO:
      return {
        title: 'Marcar como no cobrado',
        confirmBody: `¿Marcar ${count} protocolos como no cobrados?`,
        confirmLabel: 'Marcar como no cobrado',
      };
    case BulkActionType.CERRAR:
      return {
        title: 'Cerrar protocolos',
        confirmBody: '',
        confirmLabel: 'Cerrar protocolos',
      };
  }
}

interface BulkActionDialogProps {
  open: boolean;
  phase: 'confirm' | 'progress';
  actionType: BulkActionType;
  count: number;
  processed: number;
  total: number;
  onConfirm: () => void;
  onCancel: () => void;
  onBackground: () => void;
}

export function BulkActionDialog({
  open,
  phase,
  actionType,
  count,
  processed,
  total,
  onConfirm,
  onCancel,
  onBackground,
}: BulkActionDialogProps): JSX.Element {
  const copy = getCopy(actionType, count);
  const progressValue = total > 0 ? (processed / total) * 100 : 0;

  return (
    <Dialog open={open} onClose={phase === 'confirm' ? onCancel : undefined} maxWidth="sm" fullWidth>
      <DialogTitle>{copy.title}</DialogTitle>

      {phase === 'confirm' && (
        <>
          <DialogContent>
            {actionType === BulkActionType.CERRAR ? (
              <>
                <Typography variant="body2" gutterBottom>
                  Estás por cerrar {count} protocolos.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Se va a generar el informe final de cada uno. Después del cierre, los
                  protocolos quedan bloqueados para edición y eliminación.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Los informes no se envían en esta acción.
                </Typography>
              </>
            ) : (
              <Typography variant="body2">{copy.confirmBody}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Cancelar</Button>
            <Button onClick={onConfirm} variant="contained" color="primary">
              {copy.confirmLabel}
            </Button>
          </DialogActions>
        </>
      )}

      {phase === 'progress' && (
        <>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Procesando protocolos...
            </Typography>
            <LinearProgress variant="determinate" value={progressValue} sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {processed} / {total}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Podés cerrar este dialog si querés seguir trabajando. La operación va a
              continuar en segundo plano.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={onBackground}>Cerrar en segundo plano</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
